import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = ({ formRef }) => {
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill out all fields.");
      return;
    }
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message received. We'll be in touch.");
      setContact({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setContactLoading(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Enter your email to subscribe.");
      return;
    }
    setNewsletterLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("You're in. Welcome to the movement.");
      setNewsletterEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Subscription failed.";
      toast.error(typeof detail === "string" ? detail : "Subscription failed.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={formRef}
      data-testid="contact-section"
      className="relative bg-black border-t border-neutral-900 py-24 md:py-40"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          {/* Left huge text */}
          <div className="md:col-span-6">
            <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-6">
              — Get Involved
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
            >
              Join the
              <br />
              <span className="aa-outline-text">movement.</span>
            </motion.h2>

            <p className="text-neutral-400 max-w-md mt-8 font-light leading-relaxed">
              Athletes, partners, mentors — if you believe in the bridge between
              Africa and America, we want to hear from you.
            </p>

            <div className="mt-12 space-y-3 text-sm text-neutral-300">
              <div className="flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 w-20 mt-1">
                  Email
                </span>
                <a
                  href="mailto:afroambasketball@gmail.com"
                  className="text-white hover:underline"
                  data-testid="contact-email-link"
                >
                  afroambasketball@gmail.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 w-20 mt-1">
                  Web
                </span>
                <a
                  href="https://afroamhoopsglobal.us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:underline"
                  data-testid="contact-web-link"
                >
                  afroamhoopsglobal.us
                </a>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 w-20 mt-1">
                  Home HQ
                </span>
                <span className="text-white">Las Vegas, Nevada</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 w-20 mt-1">
                  SW Office
                </span>
                <span className="text-white">
                  708 Main Street, Suite 1000
                  <br />
                  Houston, Texas 77002
                </span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 w-20 mt-1">
                  PC Office
                </span>
                <span className="text-white">
                  470 Noor Street, Suite B
                  <br />
                  South San Francisco, California 94080
                </span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 w-20 mt-1">
                  Press
                </span>
                <a
                  href="mailto:afroambasketball@gmail.com"
                  className="text-white hover:underline"
                  data-testid="contact-press-link"
                >
                  afroambasketball@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* Right forms */}
          <div className="md:col-span-6">
            {/* Contact form */}
            <form
              onSubmit={submitContact}
              data-testid="contact-form"
              className="space-y-8"
            >
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                  Name
                </label>
                <input
                  type="text"
                  value={contact.name}
                  onChange={(e) =>
                    setContact({ ...contact, name: e.target.value })
                  }
                  data-testid="contact-input-name"
                  placeholder="Your full name"
                  className="w-full mt-2 bg-transparent border-b border-neutral-700 focus:border-white text-white py-3 outline-none placeholder:text-neutral-700 transition-colors"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                  Email
                </label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) =>
                    setContact({ ...contact, email: e.target.value })
                  }
                  data-testid="contact-input-email"
                  placeholder="you@example.com"
                  className="w-full mt-2 bg-transparent border-b border-neutral-700 focus:border-white text-white py-3 outline-none placeholder:text-neutral-700 transition-colors"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                  Message
                </label>
                <textarea
                  rows={4}
                  value={contact.message}
                  onChange={(e) =>
                    setContact({ ...contact, message: e.target.value })
                  }
                  data-testid="contact-input-message"
                  placeholder="Tell us what you're building or why you're reaching out…"
                  className="w-full mt-2 bg-transparent border-b border-neutral-700 focus:border-white text-white py-3 outline-none placeholder:text-neutral-700 resize-none transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={contactLoading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-neutral-200 transition-colors disabled:opacity-60"
              >
                {contactLoading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                )}
                Send Message
              </button>
            </form>

            {/* Newsletter */}
            <div className="mt-16 pt-12 border-t border-neutral-900">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
                Newsletter — Field Reports
              </div>
              <h3 className="font-display uppercase text-2xl md:text-3xl text-white mb-6">
                Stay close to the game.
              </h3>
              <form
                onSubmit={submitNewsletter}
                data-testid="newsletter-form"
                className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-stretch"
              >
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  data-testid="newsletter-input"
                  placeholder="Enter your email"
                  className="flex-1 bg-transparent border-b sm:border-b sm:border-r-0 border-neutral-700 focus:border-white text-white py-3 outline-none placeholder:text-neutral-700"
                />
                <button
                  type="submit"
                  disabled={newsletterLoading}
                  data-testid="newsletter-submit"
                  className="bg-white text-black px-6 py-3 text-xs uppercase tracking-[0.28em] font-bold hover:bg-neutral-200 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {newsletterLoading ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : null}
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
